import { useState, useRef, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: "square" | "video" | "auto";
  priority?: boolean;
  sizes?: string;
  onLoad?: () => void;
  onError?: () => void;
  placeholder?: string;
}

export const OptimizedImage = ({
  src,
  alt,
  className = "",
  aspectRatio = "auto",
  priority = false,
  sizes,
  onLoad,
  onError,
  placeholder = "/placeholder.svg"
}: OptimizedImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "50px" // Start loading 50px before the image enters the viewport
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case "square":
        return "aspect-square";
      case "video":
        return "aspect-video";
      default:
        return "";
    }
  };

  const shouldLoad = isInView && src && src !== placeholder;

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${getAspectRatioClass()} ${className}`}
      style={{ contentVisibility: "auto" }}
    >
      {isLoading && shouldLoad && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}
      
      {shouldLoad && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`w-full h-full object-contain transition-opacity duration-300 ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "low"}
          sizes={sizes}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {(!shouldLoad || hasError) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          {hasError ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <AlertCircle className="h-8 w-8 mb-2" />
              <span className="text-xs">Image not available</span>
            </div>
          ) : (
            <img
              src={placeholder}
              alt={alt}
              className="w-full h-full object-cover opacity-50"
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      )}
    </div>
  );
};